<script setup lang="ts">
import { computed, ref } from "vue";
import { Icon } from "@iconify/vue";

interface ColumnOption {
  key: string;
  label: string;
}

const props = withDefaults(
  defineProps<{
    /** Semua kolom yang tersedia (urutan = urutan definisi kolom) */
    options: ColumnOption[];
    /** Kunci kolom yang tampil, URUTannya menentukan urutan kolom di tabel */
    visibleKeys: string[];
    availableTitle?: string;
    displayedTitle?: string;
    /** Minimal jumlah kolom yang harus tetap tampil. Default 1. */
    minVisible?: number;
  }>(),
  {
    availableTitle: "Available",
    displayedTitle: "Displayed",
    minVisible: 1,
  }
);

const emit = defineEmits<{
  (e: "update:visibleKeys", keys: string[]): void;
}>();

// --- Pencarian per panel
const availableSearch = ref("");
const displayedSearch = ref("");

const visibleSet = computed(() => new Set(props.visibleKeys));

const availableList = computed(() => {
  const q = availableSearch.value.trim().toLowerCase();
  return props.options
    .filter((o) => !visibleSet.value.has(o.key))
    .filter((o) => !q || o.label.toLowerCase().includes(q));
});

const displayedList = computed(() => {
  const q = displayedSearch.value.trim().toLowerCase();
  return props.visibleKeys
    .map((key) => props.options.find((o) => o.key === key))
    .filter((o): o is ColumnOption => !!o)
    .filter((o) => !q || o.label.toLowerCase().includes(q));
});

// --- Toggle tampil/sembunyi (checkbox)
function onToggle(key: string, next: boolean) {
  if (next) {
    if (visibleSet.value.has(key)) return;
    emit("update:visibleKeys", [...props.visibleKeys, key]);
  } else {
    if (props.visibleKeys.length <= props.minVisible) return;
    emit(
      "update:visibleKeys",
      props.visibleKeys.filter((k) => k !== key)
    );
  }
}

function isRemoveDisabled(): boolean {
  return props.visibleKeys.length <= props.minVisible;
}

// --- Drag & drop urutan di panel kanan (seluruh baris draggable, hitbox besar).
// Simpan key (bukan index) agar tetap benar saat panel sedang difilter pencarian.
const dragKey = ref<string | null>(null);
// Firefox (dulu) memicu event click tepat setelah drag berakhir; klik dalam rentang
// singkat setelah dragend diabaikan supaya reorder tidak diikuti "keluar dari panel".
const lastDragEnd = ref(0);

function onRowToggle(key: string) {
  if (Date.now() - lastDragEnd.value < 150) return;
  onToggle(key, false);
}

function onAvailableRowClick(key: string) {
  if (Date.now() - lastDragEnd.value < 150) return;
  onToggle(key, true);
}

function onDragStart(option: ColumnOption, event: DragEvent) {
  dragKey.value = option.key;
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", option.key);
  }
}

function onDragOver(option: ColumnOption, event: DragEvent) {
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = "move";

  if (!dragKey.value) return;

  const keys = props.visibleKeys;
  const from = keys.indexOf(dragKey.value);
  const hovered = keys.indexOf(option.key);

  if (from < 0 || hovered < 0 || from === hovered) return;

  // Deteksi apakah pointer di setengah ATAS atau BAWAH baris yang di-hover,
  // supaya item di-droppable tepat sebelum / sesudah target (stabil, tidak bolak-balik).
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
  const beforeTarget = event.clientY < rect.top + rect.height / 2;

  // Hitung posisi insert pada list TANPA item yang sedang di-drag
  const dragged = dragKey.value;
  const next = [...keys];
  next.splice(from, 1);

  let to = hovered > from ? hovered - 1 : hovered;
  if (!beforeTarget) to += 1;

  next.splice(to, 0, dragged);

  // Tidak ada perubahan urutan -> jangan emit (cegah oscilasi)
  if (to === from) return;

  emit("update:visibleKeys", next);
  // dragKey dipertahankan: identitas item yang di-drag tetap sama sepanjang sesi drag.
}

function onDrop(event: DragEvent) {
  event.preventDefault();
  dragKey.value = null;
}

function onDragEnd() {
  lastDragEnd.value = Date.now();
  dragKey.value = null;
}

// Pointer di area kosong di bawah baris -> pindahkan item ke posisi paling akhir.
// Hanya berlaku kalau event benar-benar di container (bukan bubble dari baris).
function onContainerDragOver(event: DragEvent) {
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
  if (!dragKey.value || event.target !== event.currentTarget) return;

  const keys = props.visibleKeys;
  const from = keys.indexOf(dragKey.value);
  if (from < 0 || from === keys.length - 1) return;

  const next = [...keys];
  next.splice(from, 1);
  next.push(dragKey.value);

  emit("update:visibleKeys", next);
}
</script>

<template>
  <div class="border grid grid-cols-2 rounded-md overflow-hidden">
    <!-- Panel kiri: kolom tersedia (tersembunyi) -->
    <div class="border-r flex flex-col min-h-0">
      <div class="py-1.5 px-3 bg-gray-50 border-b flex items-center justify-between gap-2">
        <p class="font-medium shrink-0">
          {{ availableTitle }}
          <span class="text-gray-400 font-normal">({{ availableList.length }})</span>
        </p>
      </div>
      <div class="py-1.5 px-2 border-b">
        <n-input v-model:value="availableSearch" size="small" clearable :placeholder="availableTitle">
          <template #prefix>
            <Icon icon="mdi:magnify" class="text-gray-400" />
          </template>
        </n-input>
      </div>

      <div class="grow overflow-y-auto max-h-64 min-h-32">
        <div
          v-for="o in availableList"
          :key="o.key"
          class="px-3 py-1.5 flex items-center gap-2 hover:bg-gray-50 transition-colors select-none cursor-pointer"
          :class="{ 'border-b border-gray-100': true }"
          @click="onAvailableRowClick(o.key)"
        >
          <n-checkbox
            :checked="false"
            @update:checked="(v: boolean) => onToggle(o.key, v)"
            @click.stop
          />
          <span class="grow select-none">{{ o.label }}</span>
        </div>

        <div v-if="availableList.length === 0" class="px-3 py-6 text-center text-gray-400 text-sm">
          -
        </div>
      </div>
    </div>

    <!-- Panel kanan: kolom tampil (urutannya = urutan kolom tabel) -->
    <div class="flex flex-col min-h-0">
      <div class="py-1.5 px-3 bg-gray-50 border-b flex items-center justify-between gap-2">
        <p class="font-medium shrink-0">
          {{ displayedTitle }}
          <span class="text-gray-400 font-normal">({{ displayedList.length }})</span>
        </p>
      </div>
      <div class="py-1.5 px-2 border-b">
        <n-input v-model:value="displayedSearch" size="small" clearable :placeholder="displayedTitle">
          <template #prefix>
            <Icon icon="mdi:magnify" class="text-gray-400" />
          </template>
        </n-input>
      </div>

      <div class="grow overflow-y-auto max-h-64 min-h-32" @dragover="onContainerDragOver" @drop="onDrop">
        <div
          v-for="(o, idx) in displayedList"
          :key="o.key"
          draggable="true"
          @dragstart="onDragStart(o, $event)"
          @dragenter.prevent
          @dragover="onDragOver(o, $event)"
          @drop="onDrop($event)"
          @dragend="onDragEnd"
          class="px-3 py-1.5 flex items-center gap-2 transition-colors select-none cursor-pointer"
          :class="{
            'hover:bg-gray-100': true,
            'border-b border-gray-100': idx != displayedList.length - 1,
            'opacity-40': dragKey === o.key,
          }"
          @click="onRowToggle(o.key)"
        >
          <n-checkbox
            :checked="true"
            :disabled="isRemoveDisabled()"
            @update:checked="(v: boolean) => onToggle(o.key, v)"
            @click.stop
          />
          <span class="grow select-none">{{ o.label }}</span>
        </div>

        <div v-if="displayedList.length === 0" class="px-3 py-6 text-center text-gray-400 text-sm">
          -
        </div>
      </div>
    </div>
  </div>
</template>
